import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage analysis timer logic
 * Handles tracking analysis start time and showing retry button after 5 minutes
 */
export const useAnalysisTimer = (selectedApplicantForAnalysis) => {
    const [analysisStartTime, setAnalysisStartTime] = useState(null);
    const [retryInitiated, setRetryInitiated] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Reset retry state when analysis is no longer in progress
    const resetRetryState = useCallback(() => {
        setRetryInitiated(false);
        setAnalysisStartTime(null);
    }, []);

    // Set analysis start time when analysis begins
    const setStartTime = useCallback(() => {
        const backendTime = selectedApplicantForAnalysis?.updated_at;
        const startTime = backendTime ? new Date(backendTime) : new Date();
        setAnalysisStartTime(startTime);
    }, [selectedApplicantForAnalysis?.updated_at]);

    // Track when analysis starts and set up timer
    useEffect(() => {
        const analysisStatus = selectedApplicantForAnalysis?.analysis_status;

        try {
            // Set analysis start time when analysis begins
            if (analysisStatus === "IN_PROGRESS" && !analysisStartTime) {
                setStartTime();
            }
            // Reset when analysis is no longer in progress
            else if (analysisStatus !== "IN_PROGRESS") {
                resetRetryState();
                return;
            }

            // Set up timer only if we have a start time and analysis is in progress
            if (analysisStartTime && analysisStatus === "IN_PROGRESS" && !retryInitiated) {
                const now = new Date();
                const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

                // If already past 5 minutes, show button immediately
                if (analysisStartTime < fiveMinutesAgo) {
                    setForceUpdate(prev => prev + 1);
                    return;
                }

                // Calculate exact time until 5 minutes is reached
                const timeUntil5Minutes = (analysisStartTime.getTime() + 5 * 60 * 1000) - now.getTime();

                const timeoutId = setTimeout(() => {
                    setForceUpdate(prev => prev + 1);
                }, timeUntil5Minutes);

                return () => clearTimeout(timeoutId);
            }
        } catch (error) {
            console.error('Error in analysis timer:', error);
            resetRetryState();
        }
    }, [selectedApplicantForAnalysis?.analysis_status, selectedApplicantForAnalysis?.updated_at, retryInitiated, analysisStartTime, setStartTime, resetRetryState]);

    // Determine if retry button should be shown
    const shouldShowRetryButton = useCallback(() => {
        const analysisStatus = selectedApplicantForAnalysis?.analysis_status;

        if (retryInitiated) {
            return false;
        }

        if (analysisStatus === "FAILED") {
            return true;
        }

        if (analysisStatus === "IN_PROGRESS" && analysisStartTime) {
            const now = new Date();
            const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
            return analysisStartTime < fiveMinutesAgo;
        }

        return false;
    }, [selectedApplicantForAnalysis?.analysis_status, retryInitiated, analysisStartTime]);

    // Mark retry as initiated
    const initiateRetry = useCallback(() => {
        setRetryInitiated(true);
    }, []);

    return {
        shouldShowRetryButton: shouldShowRetryButton(),
        initiateRetry,
        forceUpdate
    };
};
